import type { Idioma } from "@/lib/landing/idioma"

/**
 * Los textos fijos del mercado y de los distribuidores, en los dos idiomas.
 *
 * Lo que viene de la base de datos (nombres de tienda, productos, descripciones,
 * plazos de envío…) no se traduce: es de cada vendedor. Esto es solo la
 * interfaz. El castellano es el de siempre, tal cual.
 */

const cuantos = (n: number, uno: string, varios: string) => (n === 1 ? `1 ${uno}` : `${n} ${varios}`)

const ES = {
  // Marco y piezas
  agotado: "Agotado",
  logoDe: (nombre: string) => `Logo de ${nombre}`,
  productos: (n: number) => cuantos(n, "producto", "productos"),
  envioADomicilio: "Envío a domicilio:",
  gratis: "gratis",
  gratisDesde: (importe: string) => `, gratis a partir de ${importe}`,
  soloDentroDe: (comunidad: string) => `. Solo dentro de ${comunidad}.`,
  recogidaEnElLocal: "Recogida en el local:",
  sinCoste: " sin coste",
  enDireccion: (direccion: string) => `, en ${direccion}`,
  sinFormaDeEntrega: "Este vendedor todavía no ha indicado cómo entrega.",
  bandaTitulo: "¿Haces producto local?",
  bandaTexto:
    "Si llevas tu tienda online con Zindra, tus productos pueden estar aquí: se anuncian con tu localidad, se cobran en tu cuenta y cada venta descuenta sola de tu almacén.",
  bandaBoton: "Quiero vender en el mercado",
  contacto: "/contacto",
  cesta: "Cesta",

  // Isla
  mercado: "Mercado",
  metaIslaTitulo: (isla: string) => `Café de especialidad en ${isla} | Zindra`,
  metaIslaOg: (isla: string) => `Café de especialidad en ${isla}`,
  metaIslaDescripcion: (isla: string) =>
    `Café de especialidad de ${isla}, comprado directamente a quien lo tuesta, y más producto local. Con la localidad de cada vendedor y entrega solo dentro de la comunidad.`,
  etiquetaMercado: "Mercado de producto local",
  islaTitulo: (isla: string) => `Café de especialidad en ${isla}`,
  islaIntro: "Café tostado aquí y comprado directamente a quien lo tuesta, con su localidad a la vista.",
  soloSeEntregaDentroDe: (comunidad: string) => ` Solo se entrega dentro de ${comunidad}.`,
  soloSeEntregaEnSuComunidad: " Solo se entrega dentro de la comunidad de cada vendedor.",
  islaVacia: (isla: string) => `Todavía no hay productos a la venta en ${isla}. Los primeros vendedores están preparando su puesto.`,
  queHay: "Qué hay",
  porTipo: "Por tipo de producto.",
  aLaVentaAhora: "A la venta ahora",
  quienVende: "Quién vende",
  cadaVendedorPrepara: "Cada vendedor prepara y entrega sus propios pedidos.",
  porMunicipio: "Por municipio",
  deDondeSale: (isla: string) => `De dónde sale cada cosa en ${isla}.`,
  compraParaNegocio: "¿Compras para un bar, una cafetería o una tienda?",
  distribuidoresEn: (isla: string) => `Distribuidores de café en ${isla}`,
  conSuTarifa: ", con su tarifa para negocios.",

  // Segundo tramo
  productoLocalDe: (municipio: string) => `Producto local de ${municipio}`,
  municipioDescripcionMeta: (municipio: string, isla: string) =>
    `Lo que hacen y venden los negocios de ${municipio} (${isla}), comprado directamente a ellos.`,
  municipioIntro: (municipio: string) => `Lo que hacen y venden los negocios de ${municipio}, comprado directamente a ellos.`,
  sufijoMercado: " | Mercado de Zindra",
  categoriaEnIsla: (categoria: string, isla: string) => `${categoria} en ${isla}`,
  quienLoVende: "Quién lo vende",
  tiendasDe: (lugar: string) => `Tiendas de ${lugar}`,
  cadaUnaPrepara: "Cada una prepara y entrega sus propios pedidos.",
  etiquetaMercadoDe: (isla: string) => `Mercado de producto local · ${isla}`,
  seccionVacia: (isla: string) => `Todavía no hay nada a la venta en esta sección. Vuelve pronto, o mira el resto del mercado de ${isla}.`,
  porTipoDeProducto: "Por tipo de producto",

  // Vendedor
  vendedorTitulo: (nombre: string, municipio: string) => `${nombre} · Producto local de ${municipio}`,
  vendedorDescripcion: (nombre: string, municipio: string) =>
    `Los productos de ${nombre}, de ${municipio}, comprados directamente a quien los hace.`,
  activandoPago:
    "Este vendedor está terminando de activar el pago con tarjeta. Sus productos se pueden ver, y muy pronto también comprar.",
  entrega: "Entrega",
  dondeEsta: "Dónde está",
  verEnElMapa: "Ver en el mapa",
  vende: "Vende: ",
  vendedorVacio: (nombre: string) => `${nombre} todavía no ha puesto productos a la venta.`,

  // Producto
  productoDescripcion: (titulo: string, vendedor: string, municipio: string) =>
    `${titulo}, de ${vendedor} (${municipio}). Producto local.`,
  de: "De",
  comoLlega: "Cómo llega",
  loPreparaYEntrega: (vendedor: string, donde: string) =>
    `Lo prepara y lo entrega ${vendedor}, desde ${donde}. El pago se hace con tarjeta, en una página segura de Stripe.`,
  masDe: (nombre: string) => `Más de ${nombre}`,
  verTodo: "Ver todo",

  // Comprar
  sinFormatos: "Este producto no tiene formatos a la venta.",
  formato: "Formato",
  unico: "Único",
  terminandoDeActivar: (nombre: string) => `${nombre} está terminando de activar el pago con tarjeta. Muy pronto se podrá comprar aquí.`,
  agotadoPorAhora: "Agotado por ahora.",
  quitarUno: "Quitar uno",
  anadirUno: "Añadir uno",
  anadirALaCesta: "Añadir a la cesta",
  quedan: (n: number) => (n === 1 ? "Queda 1." : `Quedan ${n}.`),
  anadido: "Añadido.",
  verCestaYPagar: "Ver la cesta y pagar",
  otroVendedorEnCesta: (nombre: string) =>
    `Tu cesta tiene productos de ${nombre}. Cada vendedor cobra y entrega por separado, así que una cesta solo puede ser de uno.`,
  otroVendedor: "otro vendedor",
  vaciarYAnadir: "Vaciar la cesta y añadir este",
  terminarAntes: "Terminar antes la otra compra",

  // Cesta
  tuCesta: "Tu cesta",
  metaCesta: "Tu cesta | Mercado de Zindra",
  noSeHaPodidoCargar: "No se ha podido cargar la cesta.",
  ajustaAntesDePagar: "Ajusta las cantidades de lo que ya no está disponible antes de pagar.",
  noSeHaPodidoIniciar: "No se ha podido iniciar el pago. Prueba de nuevo.",
  revisaLaConexion: "No se ha podido iniciar el pago. Revisa la conexión y prueba de nuevo.",
  cargandoCesta: "Cargando la cesta…",
  reintentar: "Reintentar",
  cestaVacia: "Tu cesta está vacía.",
  yaNoEstaALaVenta: "Lo que tenías guardado ya no está a la venta.",
  irAlMercado: "Ir al mercado",
  vendidoPor: "Vendido y entregado por",
  quitar: (titulo: string) => `Quitar ${titulo}`,
  yaNoQueda: "Ya no queda. Quítalo para poder pagar.",
  soloQuedan: (n: number) => `Solo ${n === 1 ? "queda 1" : `quedan ${n}`}. `,
  noQuedaSuficiente: "No queda suficiente junto con el resto de la cesta. Baja la cantidad.",
  dejar: (n: number) => `Dejar ${n}`,
  comoLoQuieres: "Cómo lo quieres recibir",
  envioADomicilioOpcion: "Envío a domicilio",
  recogidaEnElLocalOpcion: "Recogida en el local",
  gratisMayuscula: "Gratis",
  soloDentroDeOpcion: (comunidad: string) => `Solo dentro de ${comunidad}.`,
  tusDatos: "Tus datos",
  nombreYApellidos: "Nombre y apellidos",
  correo: "Correo",
  telefono: "Teléfono",
  opcional: " (opcional)",
  direccionDeEntrega: "Dirección de entrega",
  calleYNumero: "Calle y número",
  pisoPuerta: "Piso, puerta, indicaciones (opcional)",
  codigoPostal: "Código postal",
  poblacion: "Población",
  notaParaElVendedor: "Nota para el vendedor (opcional)",
  resumen: "Resumen",
  productosResumen: "Productos",
  envio: "Envío",
  conMasEnvioGratis: (importe: string) => `Con ${importe} más, el envío es gratis.`,
  total: "Total",
  abriendoElPago: "Abriendo el pago…",
  pagar: (importe: string) => `Pagar ${importe} con tarjeta`,
  cestaGuardada: (nombre: string) => `${nombre} está terminando de activar el pago con tarjeta. Tu cesta se queda guardada.`,
  pagoSeguro: (nombre: string) =>
    `El pago se hace en una página segura de Stripe y lo cobra directamente ${nombre}, que es quien te vende y te entrega el pedido. Zindra no guarda los datos de tu tarjeta.`,

  // Distribuidores
  distribuidores: "Distribuidores",
  metaDistribuidoresDescripcion: (isla: string) =>
    `Tostadores y distribuidores de café de ${isla} que sirven a bares, cafeterías, restaurantes, hoteles y tiendas, con su tarifa para negocios y su localidad a la vista.`,
  sufijoZindra: " | Zindra",
  desde: (importe: string) => `· desde ${importe}`,
  etiquetaNegocios: "Mercado para negocios",
  distribuidoresIntro: (isla: string) =>
    `Quién sirve café en ${isla} a bares, cafeterías, restaurantes, hoteles y tiendas, con su tarifa para negocios a la vista. El pedido se hace directamente con cada distribuidor.`,
  distribuidoresVacio: (isla: string) =>
    `Todavía no hay distribuidores de café de ${isla} con su tarifa publicada. Los primeros están preparándola.`,
  cadaUnoConSuTarifa: "Cada uno con su localidad y su tarifa para negocios.",
  compraParaCasa: "¿Compras para casa y no para un negocio?",
  cafeDeEspecialidadEn: (isla: string) => `Café de especialidad en ${isla}`,
  compradoAQuienLoTuesta: ", comprado directamente a quien lo tuesta.",
  distribuidorTitulo: (nombre: string, municipio: string) => `${nombre} · Distribuidor de café en ${municipio}`,
  distribuidorDescripcion: (nombre: string, municipio: string) =>
    `La tarifa para negocios de ${nombre}, de ${municipio}: café para bares, cafeterías, restaurantes, hoteles y tiendas.`,
  distribuidorEn: (municipio: string) => `Distribuidor de café en ${municipio}`,
  formatoUnico: "Formato único",
  enTienda: (importe: string) => `En tienda: ${importe}`,
  paraHacerUnPedido: "Para hacer un pedido",
  hablaDirectamente: (nombre: string) => `Habla directamente con ${nombre}: te confirma precios, cantidades y entrega.`,
  condiciones: "Condiciones",
  sinTarifa: (nombre: string) => `${nombre} todavía no ha publicado su tarifa para negocios.`,
  tarifaParaNegocios: "Tarifa para negocios",
  actualizadaEl: (fecha: string) => `Actualizada el ${fecha}.`,
  preciosConIva: "Precios con IVA incluido.",
  preciosSinIva: "Precios sin IVA.",
  locale: "es-ES",
}

type Textos = typeof ES

const EN: Textos = {
  agotado: "Sold out",
  logoDe: (nombre) => `${nombre} logo`,
  productos: (n) => cuantos(n, "product", "products"),
  envioADomicilio: "Home delivery:",
  gratis: "free",
  gratisDesde: (importe) => `, free on orders over ${importe}`,
  soloDentroDe: (comunidad) => `. Only within ${comunidad}.`,
  recogidaEnElLocal: "Collection in store:",
  sinCoste: " free",
  enDireccion: (direccion) => `, at ${direccion}`,
  sinFormaDeEntrega: "This seller hasn't set up delivery or collection yet.",
  bandaTitulo: "Do you make local products?",
  bandaTexto:
    "If you run your online shop with Zindra, your products can be listed here: shown with your town, paid straight into your account, and every sale deducted from your stock automatically.",
  bandaBoton: "I want to sell on the market",
  contacto: "/en/contact",
  cesta: "Basket",

  mercado: "Market",
  metaIslaTitulo: (isla) => `Specialty Coffee in ${isla}, Direct from Local Roasters | Zindra`,
  metaIslaOg: (isla) => `Specialty coffee in ${isla}`,
  metaIslaDescripcion: (isla) =>
    `Buy specialty coffee roasted in ${isla} direct from the roasters, plus other local products. Every seller's town is shown, and orders are delivered within the region.`,
  etiquetaMercado: "Local produce market",
  islaTitulo: (isla) => `Specialty coffee in ${isla}`,
  islaIntro: "Coffee roasted here and bought direct from the people who roast it, with their town on show.",
  soloSeEntregaDentroDe: (comunidad) => ` Delivery only within ${comunidad}.`,
  soloSeEntregaEnSuComunidad: " Each seller delivers only within their own region of Spain.",
  islaVacia: (isla) => `There's nothing for sale in ${isla} yet. The first sellers are setting up their stalls.`,
  queHay: "What's on offer",
  porTipo: "By type of product.",
  aLaVentaAhora: "For sale now",
  quienVende: "Who's selling",
  cadaVendedorPrepara: "Each seller prepares and delivers their own orders.",
  porMunicipio: "By town",
  deDondeSale: (isla) => `Where everything comes from in ${isla}.`,
  compraParaNegocio: "Buying for a bar, café or shop?",
  distribuidoresEn: (isla) => `Coffee suppliers in ${isla}`,
  conSuTarifa: ", with their trade prices.",

  productoLocalDe: (municipio) => `Local products from ${municipio}`,
  municipioDescripcionMeta: (municipio, isla) =>
    `What businesses in ${municipio} (${isla}) make and sell, bought direct from them.`,
  municipioIntro: (municipio) => `What businesses in ${municipio} make and sell, bought direct from them.`,
  sufijoMercado: " | Zindra Market",
  categoriaEnIsla: (categoria, isla) => `${categoria} in ${isla}`,
  quienLoVende: "Who sells it",
  tiendasDe: (lugar) => `Shops in ${lugar}`,
  cadaUnaPrepara: "Each one prepares and delivers its own orders.",
  etiquetaMercadoDe: (isla) => `Local produce market · ${isla}`,
  seccionVacia: (isla) => `There's nothing for sale in this section yet. Come back soon, or browse the rest of the ${isla} market.`,
  porTipoDeProducto: "By type of product",

  vendedorTitulo: (nombre, municipio) => `${nombre} · Local products from ${municipio}`,
  vendedorDescripcion: (nombre, municipio) => `Products from ${nombre} in ${municipio}, bought direct from the people who make them.`,
  activandoPago:
    "This seller is still setting up card payments. You can browse their products now, and buy them very soon.",
  entrega: "Delivery",
  dondeEsta: "Where to find them",
  verEnElMapa: "View on the map",
  vende: "Sold by: ",
  vendedorVacio: (nombre) => `${nombre} hasn't listed any products yet.`,

  productoDescripcion: (titulo, vendedor, municipio) => `${titulo}, from ${vendedor} (${municipio}). Local produce.`,
  de: "From",
  comoLlega: "Delivery",
  loPreparaYEntrega: (vendedor, donde) =>
    `Prepared and delivered by ${vendedor}, from ${donde}. You pay by card on a secure Stripe page.`,
  masDe: (nombre) => `More from ${nombre}`,
  verTodo: "See all",

  sinFormatos: "This product has no options for sale.",
  formato: "Option",
  unico: "Single",
  terminandoDeActivar: (nombre) => `${nombre} is still setting up card payments. You'll be able to buy here very soon.`,
  agotadoPorAhora: "Sold out for now.",
  quitarUno: "Remove one",
  anadirUno: "Add one",
  anadirALaCesta: "Add to basket",
  quedan: (n) => (n === 1 ? "Only 1 left." : `Only ${n} left.`),
  anadido: "Added.",
  verCestaYPagar: "View basket and pay",
  otroVendedorEnCesta: (nombre) =>
    `Your basket has products from ${nombre}. Each seller charges and delivers separately, so a basket can only hold one seller's products.`,
  otroVendedor: "another seller",
  vaciarYAnadir: "Empty the basket and add this",
  terminarAntes: "Finish the other order first",

  tuCesta: "Your basket",
  metaCesta: "Your basket | Zindra Market",
  noSeHaPodidoCargar: "We couldn't load your basket.",
  ajustaAntesDePagar: "Adjust the quantities of anything no longer available before paying.",
  noSeHaPodidoIniciar: "We couldn't start the payment. Please try again.",
  revisaLaConexion: "We couldn't start the payment. Check your connection and try again.",
  cargandoCesta: "Loading your basket…",
  reintentar: "Try again",
  cestaVacia: "Your basket is empty.",
  yaNoEstaALaVenta: "What you had saved is no longer for sale.",
  irAlMercado: "Go to the market",
  vendidoPor: "Sold and delivered by",
  quitar: (titulo) => `Remove ${titulo}`,
  yaNoQueda: "None left. Remove it to pay.",
  soloQuedan: (n) => `Only ${n} left. `,
  noQuedaSuficiente: "There isn't enough left together with the rest of your basket. Lower the quantity.",
  dejar: (n) => `Keep ${n}`,
  comoLoQuieres: "How would you like to receive it?",
  envioADomicilioOpcion: "Home delivery",
  recogidaEnElLocalOpcion: "Collection in store",
  gratisMayuscula: "Free",
  soloDentroDeOpcion: (comunidad) => `Only within ${comunidad}.`,
  tusDatos: "Your details",
  nombreYApellidos: "Full name",
  correo: "Email",
  telefono: "Phone",
  opcional: " (optional)",
  direccionDeEntrega: "Delivery address",
  calleYNumero: "Street and number",
  pisoPuerta: "Flat, door, directions (optional)",
  codigoPostal: "Postcode",
  poblacion: "Town",
  notaParaElVendedor: "Note for the seller (optional)",
  resumen: "Summary",
  productosResumen: "Products",
  envio: "Delivery",
  conMasEnvioGratis: (importe) => `Add ${importe} more for free delivery.`,
  total: "Total",
  abriendoElPago: "Opening payment…",
  pagar: (importe) => `Pay ${importe} by card`,
  cestaGuardada: (nombre) => `${nombre} is still setting up card payments. Your basket will be saved.`,
  pagoSeguro: (nombre) =>
    `You pay on a secure Stripe page and the money goes straight to ${nombre}, who sells and delivers your order. Zindra doesn't store your card details.`,

  distribuidores: "Suppliers",
  metaDistribuidoresDescripcion: (isla) =>
    `Coffee roasters and wholesale suppliers in ${isla} serving bars, cafés, restaurants, hotels and shops, with their trade prices and location on show.`,
  sufijoZindra: " | Zindra",
  desde: (importe) => `· from ${importe}`,
  etiquetaNegocios: "Trade market",
  distribuidoresIntro: (isla) =>
    `Who supplies coffee in ${isla} to bars, cafés, restaurants, hotels and shops, with their trade prices on show. You order directly from each supplier.`,
  distribuidoresVacio: (isla) => `No coffee suppliers in ${isla} have published their trade prices yet. The first ones are getting them ready.`,
  cadaUnoConSuTarifa: "Each one with their location and trade prices.",
  compraParaCasa: "Buying for home rather than a business?",
  cafeDeEspecialidadEn: (isla) => `Specialty coffee in ${isla}`,
  compradoAQuienLoTuesta: ", bought direct from the people who roast it.",
  distribuidorTitulo: (nombre, municipio) => `${nombre} · Coffee supplier in ${municipio}`,
  distribuidorDescripcion: (nombre, municipio) =>
    `Trade prices from ${nombre} in ${municipio}: coffee for bars, cafés, restaurants, hotels and shops.`,
  distribuidorEn: (municipio) => `Coffee supplier in ${municipio}`,
  formatoUnico: "Single option",
  enTienda: (importe) => `Retail: ${importe}`,
  paraHacerUnPedido: "How to order",
  hablaDirectamente: (nombre) => `Contact ${nombre} directly to confirm prices, quantities and delivery.`,
  condiciones: "Terms",
  sinTarifa: (nombre) => `${nombre} hasn't published their trade prices yet.`,
  tarifaParaNegocios: "Trade prices",
  actualizadaEl: (fecha) => `Updated ${fecha}.`,
  preciosConIva: "Prices include VAT.",
  preciosSinIva: "Prices exclude VAT.",
  locale: "en-GB",
}

export const TEXTOS_MERCADO: Record<Idioma, Textos> = { es: ES, en: EN }

export function textosMercado(idioma: Idioma): Textos {
  return TEXTOS_MERCADO[idioma]
}

/**
 * Los errores que devuelve el servidor al abrir el pago vienen en castellano
 * (lib/mercado/pedidos.ts, app/api/public/mercado/**). En la cesta en inglés se
 * traducen aquí; uno que no esté en la tabla se sustituye por el genérico.
 */
const ERRORES_EN: Record<string, string> = {
  "No se ha podido cargar la cesta.": "We couldn't load your basket.",
  "La cesta no es válida.": "Your basket isn't valid.",
  "Este vendedor ya no está en el mercado.": "This seller is no longer on the market.",
  "Este vendedor no está vendiendo en el mercado ahora mismo.": "This seller isn't selling on the market right now.",
  "No se ha podido iniciar el pedido. Prueba de nuevo.": "We couldn't start your order. Please try again.",
  "El pago con tarjeta no está disponible ahora mismo.": "Card payment isn't available right now.",
  "Escribe tu nombre.": "Please enter your name.",
  "El correo no es válido.": "That email address isn't valid.",
  "Escribe la dirección de entrega.": "Please enter the delivery address.",
  "Escribe la población.": "Please enter your town.",
  "Hace falta un teléfono para la entrega.": "A phone number is needed for delivery.",
  "La cesta está vacía.": "Your basket is empty.",
  "Hay demasiados productos distintos en la cesta.": "There are too many different products in your basket.",
  "No se ha podido revisar la cesta. Prueba de nuevo.": "We couldn't check your basket. Please try again.",
  "Algún producto de la cesta ya no está a la venta. Quítalo para continuar.":
    "A product in your basket is no longer for sale. Remove it to continue.",
  "No queda suficiente de algún producto de la cesta. Ajusta las cantidades.":
    "There isn't enough left of a product in your basket. Adjust the quantities.",
  "Tienes varios pagos sin terminar. Espera unos minutos y vuelve a intentarlo.":
    "You have several unfinished payments. Wait a few minutes and try again.",
  "El importe no es válido.": "The amount isn't valid.",
  "Alguien acaba de llevarse lo último de algún producto de la cesta. Ajusta las cantidades.":
    "Someone has just bought the last of a product in your basket. Adjust the quantities.",
  "No se ha podido iniciar el pago con tarjeta. Prueba de nuevo.": "We couldn't start the card payment. Please try again.",
  "Este vendedor no hace envíos.": "This seller doesn't deliver.",
  "Este vendedor no ofrece recogida en el local.": "This seller doesn't offer collection in store.",
  "El código postal no es válido.": "That postcode isn't valid.",
}

/** El error del servidor en el idioma de la página. */
export function errorEn(idioma: Idioma, error: string | null | undefined, porDefecto: string): string {
  if (!error) return porDefecto
  if (idioma === "es") return error
  if (ERRORES_EN[error]) return ERRORES_EN[error]
  if (error.startsWith("Este vendedor solo envía dentro de")) return "This seller only delivers within their own region of Spain."
  return porDefecto
}
